import { useEffect, useState } from 'react';
import { formatDate } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';

import * as adminApi from '../../api/admin';
import type { PlatformAdminDetail } from '../../api/admin';

/**
 * Read-only view of another platform admin's profile.
 *
 * Reached from the Platform admins list when the row isn't the
 * current user's. Layout mirrors the editable Profile page so the
 * mental model carries over — same Account / Profile / Roles cards,
 * same field order — but every input is `disabled` and there's no
 * Save button. We treat editing-other-admins as out-of-scope: each
 * admin owns their own profile. (If we ever introduce a tier above
 * superadmin, that's where the edit-anybody power would live.)
 */
export function ViewPlatformAdmin() {
  const { userId } = useParams();
  const [user, setUser] = useState<PlatformAdminDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    setError(null);
    adminApi
      .getPlatformAdmin(userId)
      .then(setUser)
      .catch((err: unknown) =>
        setError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'failed to load platform admin',
        ),
      );
  }, [userId]);

  const backButton = (
    <Button
      component={RouterLink}
      to="/admin/platform-admins"
      startIcon={<ArrowBackIcon />}
      sx={{ alignSelf: 'flex-start' }}
    >
      Platform Admins
    </Button>
  );

  if (error) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 880 }}>
        {backButton}
        <Alert severity="error">{error}</Alert>
      </Stack>
    );
  }
  if (!user) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 880 }}>
        {backButton}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  // Field accessor that defaults missing values to '' so MUI inputs
  // are always controlled. The fields themselves are disabled so the
  // user never types — empty + disabled is the right "no value" look.
  const v = (s: string | null) => s ?? '';

  return (
    <Stack spacing={3} sx={{ maxWidth: 880 }}>
      {backButton}
      <Typography variant="h4">{user.full_name || user.email}</Typography>
      <Typography variant="body2" color="text.secondary">
        Read-only view. Only the admin themselves can edit this profile.
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Account
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Email
              </Typography>
              <Typography>{user.email}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Email verified
              </Typography>
              {user.email_verified_at ? (
                <Chip label="Verified" color="success" size="small" />
              ) : (
                <Chip label="Pending" color="warning" size="small" />
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Member since
              </Typography>
              <Typography>
                {formatDate(user.created_at)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Profile
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Full name" fullWidth value={v(user.full_name)} disabled />

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Contact
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Phone" fullWidth value={v(user.phone)} disabled />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Mobile" fullWidth value={v(user.mobile)} disabled />
              </Grid>
            </Grid>

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Mailing address
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Address line 1"
                  fullWidth
                  value={v(user.address_line1)}
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Address line 2"
                  fullWidth
                  value={v(user.address_line2)}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="City" fullWidth value={v(user.city)} disabled />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="State / region"
                  fullWidth
                  value={v(user.state_region)}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Postal code"
                  fullWidth
                  value={v(user.postal_code)}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Country"
                  fullWidth
                  value={v(user.country)}
                  disabled
                />
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Role
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label="superadmin · platform"
              color="primary"
              variant="filled"
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
